import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePromotions } from '../../hooks/queries';
import { formatPromotionUses } from '../../utils/promotion-uses';
import { getPromotionTypeLabel } from '../../utils/promotion-display';

type FilterTab = 'all' | 'active' | 'inactive';
type PromotionType = 'coupons' | 'automatic';

export function PromotionList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [promotionType, setPromotionType] = useState<PromotionType>('automatic');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  useEffect(() => {
    setPage(1);
  }, [filterTab, appliedSearch, promotionType]);

  const handleSearchSubmit = (event?: React.FormEvent): void => {
    event?.preventDefault();
    setAppliedSearch(searchInput);
    setPage(1);
  };

  const handleClearSearch = (): void => {
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
  };

  const isPromotionActive = (promo: Record<string, unknown>): boolean => {
    if (promo.status !== undefined && promo.status !== null) {
      const status = String(promo.status).toLowerCase().trim();
      return status === 'enabled' || status === 'active';
    }
    if (promo.enabled !== undefined && promo.enabled !== null) {
      return promo.enabled === true;
    }
    if (promo.is_enabled !== undefined && promo.is_enabled !== null) {
      return promo.is_enabled === true;
    }
    return false;
  };

  const applyClientSideTransforms = (allPromotionsData: Record<string, unknown>[]): Record<string, unknown>[] => {
    const sorted = [...allPromotionsData];

    if (promotionType === 'automatic') {
      sorted.sort((a, b) => {
        const priorityA = a.priority !== undefined && a.priority !== null ? Number(a.priority) : 999999;
        const priorityB = b.priority !== undefined && b.priority !== null ? Number(b.priority) : 999999;
        return priorityA - priorityB;
      });
    } else {
      sorted.sort((a, b) => {
        const dateA = a.date_created ? new Date(String(a.date_created)).getTime() : 0;
        const dateB = b.date_created ? new Date(String(b.date_created)).getTime() : 0;
        return dateB - dateA;
      });
    }

    if (filterTab === 'active') {
      return sorted.filter((promotion) => isPromotionActive(promotion));
    }
    if (filterTab === 'inactive') {
      return sorted.filter((promotion) => !isPromotionActive(promotion));
    }

    return sorted;
  };

  const statusParam = filterTab === 'all' ? 'all' : filterTab;
  const normalizedSearch = appliedSearch.trim() || undefined;
  const { data: pageResponse, isLoading, error: pageQueryError } = usePromotions(
    page,
    limit,
    promotionType,
    statusParam,
    normalizedSearch,
  );

  const promotions = applyClientSideTransforms(
    pageResponse?.data && Array.isArray(pageResponse.data)
      ? (pageResponse.data as Record<string, unknown>[])
      : [],
  );

  const error = pageQueryError
    ? String((pageQueryError as { message?: string }).message ?? pageQueryError)
    : pageResponse?.success === false
      ? String(pageResponse.message ?? 'Failed to fetch promotions')
      : null;

  const pagination = pageResponse?.pagination;
  const totalCount = pagination?.total;
  const isSearching = Boolean(normalizedSearch);
  const hasMore =
    totalCount !== undefined
      ? page * limit < totalCount
      : promotions.length === limit;

  const detailQuery = new URLSearchParams({
    type: promotionType,
    ...(isSearching ? { searched: '1' } : {}),
  });
  const detailPath = (promoId: unknown) => `/promotions/${promoId}?${detailQuery}`;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'Not set';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Not set';
    }
  };

  if (isLoading && promotions.length === 0) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="spinner" />
          Loading promotions…
        </div>
      </div>
    );
  }

  if (error && promotions.length === 0) {
    return (
      <div className="page-container">
        <div className="error-banner">⚠ {error}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Promotions</h1>
          <p className="page-subtitle">
            {isSearching && totalCount !== undefined
              ? `${promotions.length} shown · ${totalCount} match${totalCount === 1 ? '' : 'es'}`
              : `${promotions.length} promotion${promotions.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="page-actions">
          <form
            className="search-wrap"
            onSubmit={handleSearchSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search promotions…"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Search
            </button>
            {appliedSearch.trim() ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClearSearch}
              >
                Clear
              </button>
            ) : null}
          </form>
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              ←
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page + 1)} disabled={!hasMore}>
              →
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div className="tab-bar" style={{ marginBottom: 0 }}>
          <button
            className={`tab-btn ${promotionType === 'automatic' ? 'active' : ''}`}
            onClick={() => setPromotionType('automatic')}
          >
            Automatic
          </button>
          <button
            className={`tab-btn ${promotionType === 'coupons' ? 'active' : ''}`}
            onClick={() => setPromotionType('coupons')}
          >
            Coupons
          </button>
        </div>
        <div className="tab-bar" style={{ marginBottom: 0 }}>
          <button className={`tab-btn ${filterTab === 'all' ? 'active' : ''}`} onClick={() => setFilterTab('all')}>
            All
          </button>
          <button className={`tab-btn ${filterTab === 'active' ? 'active' : ''}`} onClick={() => setFilterTab('active')}>
            Active
          </button>
          <button
            className={`tab-btn ${filterTab === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilterTab('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      {promotions.length === 0 ? (
        <div className="table-wrap">
          <div className="empty-state">
            <div className="empty-state-icon">🎟</div>
            <p className="empty-state-title">No promotions found</p>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Currency</th>
                <th>Type</th>
                <th>Uses</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map((promo) => (
                <tr key={String(promo.id)} className="clickable" onClick={() => navigate(detailPath(promo.id))}>
                  <td>
                    <Link
                      to={detailPath(promo.id)}
                      className="text-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {String(promo.name || promo.code || `Promotion #${promo.id}`)}
                    </Link>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{String(promo.currency_code || 'USD')}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{getPromotionTypeLabel(promo)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{formatPromotionUses(promo)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {formatDate(
                      (promo.start_date || promo.date_start || promo.starts_at) as string | undefined,
                    )}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {formatDate(
                      (promo.end_date || promo.date_end || promo.expires_at || promo.date_expires) as
                        | string
                        | undefined,
                    )}
                  </td>
                  <td>
                    <span className={`badge ${isPromotionActive(promo) ? 'badge-success' : 'badge-neutral'}`}>
                      {isPromotionActive(promo) ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
